import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardContent,
  Grid,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Box,
  Divider,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import {
  format,
  parseISO,
  startOfHour,
  startOfDay,
  startOfISOWeek,
  startOfMonth,
  addHours,
  addDays,
  addWeeks,
  addMonths,
  isBefore,
} from "date-fns";
import { BlenderFlowChart } from "./charts/BlenderFlowChart";
import { FlowRateChart } from "./charts/FlowRateChart";
import { StatusIndicator } from "../../common/StatusIndicator";
import { MATERIAL_COSTS_USD_PER_LB } from "../../../data/materialCosts";
import { MATERIAL_LABELS } from "../../../data/blenderLabels";
import { LoadingOverlay } from "../../common/LoadingOverlay";
import { MaterialTotalsCards } from "../../common/MaterialTotalsCards";
import { getBlenderFlow, getBlendersCurrentStatus } from "../../../api/blender";
import { asNashvilleLocal, nowAsNashvilleLocal, nashvilleLocalToISO } from "../../../utils/nashvilleTime";

// ---- local types to make TS happy ----
type BlenderOption = {
  id: string;
  label: string;
  hasFlow3?: boolean;
};

interface BlenderFlowDashboardProps {
  blenderOptions?: BlenderOption[];
}

type NormalizedFlowRow = {
  timestamp: string | number | Date;
  flow1: number | null | undefined;
  flow2: number | null | undefined;
  flow3: number | null | undefined;
};

type BlenderStatusMap = Record<
  string,
  {
    status?: "running" | "idle" | "stopped" | string;
  }
>;

// ---------- Date helpers ----------
function toValidDate(input: number | string | Date): Date | null {
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  if (typeof input === "number") {
    const ms = input > 1e12 ? input : input * 1000;
    const d = new Date(ms);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof input === "string") {
    let s = input.trim();
    if (!s.includes("T") && s.includes(" ")) s = s.replace(" ", "T");
    let d = new Date(s);
    if (!isNaN(d.getTime())) return d;
    try {
      const dt = parseISO(input);
      return isNaN(dt.getTime()) ? null : dt;
    } catch {
      return null;
    }
  }
  return null;
}

function alignStart(d: Date, type: string) {
  switch (type) {
    case "hour":
      return startOfHour(d);
    case "day":
      return startOfDay(d);
    case "week":
      return startOfISOWeek(d);
    case "month":
      return startOfMonth(d);
    default:
      return startOfDay(d);
  }
}

function addOne(d: Date, type: string) {
  switch (type) {
    case "hour":
      return addHours(d, 1);
    case "day":
      return addDays(d, 1);
    case "week":
      return addWeeks(d, 1);
    case "month":
      return addMonths(d, 1);
    default:
      return addDays(d, 1);
  }
}

function completedEnd(type: string, maybeEnd: Date | null) {
  const now = nowAsNashvilleLocal();
  const upto = maybeEnd && maybeEnd < now ? maybeEnd : now;
  return alignStart(upto, type);
}

const formatForInterval = (date: Date, type: string) => {
  switch (type) {
    case "hour":
      return format(date, "yyyy-MM-dd HH:00");
    case "day":
      return format(date, "yyyy-MM-dd");
    case "week":
      return format(startOfISOWeek(date), "yyyy-MM-dd") + " (week)";
    case "month":
      return format(date, "yyyy-MM");
    default:
      return format(date, "yyyy-MM-dd");
  }
};

// ------------- helper: decide if a blender has 3 flows -------------
const defaultHasFlow3 = (blenderId: string) => {
  return blenderId === "1" || blenderId === "3";
};

// ------------- helper: cost profile by blender -------------
const getCostPerLb = (blenderId: string) => {
  if (blenderId === "1" || blenderId === "3") {
    return {
      flow1: MATERIAL_COSTS_USD_PER_LB["LDPE-8melt"],
      flow2: MATERIAL_COSTS_USD_PER_LB["Polypropylene"],
      flow3: MATERIAL_COSTS_USD_PER_LB["Regrind"],
    };
  }
  return {
    flow1: MATERIAL_COSTS_USD_PER_LB["LDPE-8melt"],
    flow2: MATERIAL_COSTS_USD_PER_LB["LLDPE"],
    flow3: 0,
  };
};

// ------------- helper: material label profile by blender -------------
const getMaterialLabels = (blenderId: string) => {
  if (blenderId === "1" || blenderId === "3") {
    return {
      flow1: MATERIAL_LABELS.A1,
      flow2: MATERIAL_LABELS.A2,
      flow3: MATERIAL_LABELS.A3,
    };
  }
  return {
    flow1: MATERIAL_LABELS.B1,
    flow2: MATERIAL_LABELS.B2,
  } as any;
};

const BlenderFlowDashboard: React.FC<BlenderFlowDashboardProps> = ({
  blenderOptions,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const effectiveBlenderOptions: BlenderOption[] = useMemo(
    () =>
      blenderOptions ?? [
        { id: "1", label: "Blender A", hasFlow3: true },
        { id: "2", label: "Blender B", hasFlow3: false },
      ],
    [blenderOptions]
  );
  // Stable dep for effects/memos that only care about which blenders are shown.
  const blenderIdsKey = effectiveBlenderOptions.map((o) => o.id).join(",");

  // Flow rows for every blender on the line, keyed by blender id.
  const [flowByBlender, setFlowByBlender] = useState<
    Record<string, NormalizedFlowRow[]>
  >({});
  const [loadingFlow, setLoadingFlow] = useState(false);

  const [start, setStart] = useState<Date | null>(() =>
    asNashvilleLocal(new Date(Date.now() - 7 * 24 * 3600_000))
  );
  const [end, setEnd] = useState<Date | null>(() => nowAsNashvilleLocal());
  // Quick-range preset (hours); null when a custom range is set. Default 7D.
  const [rangeHrs, setRangeHrs] = useState<number | null>(168);
  const [interval, setInterval] = useState<"hour" | "day" | "week" | "month">(
    "hour"
  );
  const [unit, setUnit] = useState<"kg" | "lb" | "usd">("lb");
  const [statusById, setStatusById] = useState<
    Record<string, "running" | "idle" | "stopped" | "">
  >({});
  // Initial load only — not on every 60s poll (avoids flicker).
  const [statusLoading, setStatusLoading] = useState(true);

  // ---- status poller (all blenders on the line) ----
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const fetchStatus = async () => {
      try {
        const data = (await getBlendersCurrentStatus(
          signal
        )) as unknown as BlenderStatusMap;
        setStatusById((prev) => {
          const next = { ...prev };
          for (const opt of effectiveBlenderOptions) {
            const s = data?.[opt.id]?.status;
            if (s) next[opt.id] = s as any;
          }
          return next;
        });
      } catch {
        /* noop */
      } finally {
        setStatusLoading(false);
      }
    };

    setStatusLoading(true);
    fetchStatus();
    const intervalId: number = window.setInterval(fetchStatus, 60_000);
    return () => {
      clearInterval(intervalId);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blenderIdsKey]);

  // ---- unit conversion (cost profile depends on the blender) ----
  const convertValue = (
    valueKg: number,
    materialKey: "flow1" | "flow2" | "flow3",
    blenderId: string
  ) => {
    const lb = valueKg * 2.20462;
    if (unit === "kg") return valueKg;
    if (unit === "lb") return Number(lb.toFixed(3));
    if (unit === "usd")
      return Number(
        (lb * ((getCostPerLb(blenderId) as any)[materialKey] ?? 0)).toFixed(2)
      );
    return valueKg;
  };

  // ---- FLOW (grouped) — fetched for every blender on the line ----
  const fetchData = async () => {
    if (!start || !end) return;
    setLoadingFlow(true);
    try {
      const effEnd = completedEnd(interval, end);
      const results = await Promise.all(
        effectiveBlenderOptions.map(async (opt) => {
          const data = await getBlenderFlow({
            start: nashvilleLocalToISO(start),
            end: nashvilleLocalToISO(effEnd),
            group_by: interval,
            // API might be typed narrowly -> cast to any to allow 3/4
            blender_id: opt.id as any,
          });

          const normalized: NormalizedFlowRow[] = data.map((row: any) => ({
            timestamp: row.period,
            flow1: row.delta_value1_kg,
            flow2: row.delta_value2_kg,
            flow3: row.delta_value3_kg ?? null,
          }));
          return [opt.id, normalized] as const;
        })
      );
      setFlowByBlender(Object.fromEntries(results));
    } catch {
      setFlowByBlender({});
    } finally {
      setLoadingFlow(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blenderIdsKey, start, end, interval]);

  const unitLabel = unit === "usd" ? "$" : unit;

  // ---- chart series, per blender ----
  const chartsByBlender = useMemo(() => {
    const out: Record<string, { bar: any[]; line: any[] }> = {};
    if (!start || !end) return out;

    const cap = completedEnd(interval, end);
    const startAligned = alignStart(start, interval);
    if (!isBefore(startAligned, cap)) {
      for (const opt of effectiveBlenderOptions) out[opt.id] = { bar: [], line: [] };
      return out;
    }

    const filledKeys: number[] = [];
    for (
      let cur = new Date(startAligned);
      isBefore(cur, cap);
      cur = addOne(cur, interval)
    ) {
      filledKeys.push(cur.getTime());
    }

    for (const opt of effectiveBlenderOptions) {
      const rows = flowByBlender[opt.id] ?? [];
      const hasFlow3 = opt.hasFlow3 ?? defaultHasFlow3(opt.id);

      const rawMap = new Map<
        number,
        { flow1: number; flow2: number; flow3: number; count: number }
      >();

      for (const row of rows) {
        const d = toValidDate(row.timestamp);
        if (!d) continue;
        if (!(d < cap)) continue;

        const bucketStart = alignStart(d, interval).getTime();
        const existing = rawMap.get(bucketStart) || {
          flow1: 0,
          flow2: 0,
          flow3: 0,
          count: 0,
        };

        existing.flow1 += Number(row.flow1 || 0);
        existing.flow2 += Number(row.flow2 || 0);
        if (hasFlow3 && row.flow3 != null)
          existing.flow3 += Number(row.flow3 || 0);
        existing.count += 1;

        rawMap.set(bucketStart, existing);
      }

      const bar = filledKeys.map((keyMs) => {
        const entry = rawMap.get(keyMs) || {
          flow1: 0,
          flow2: 0,
          flow3: 0,
          count: 0,
        };
        const base = {
          timestamp: keyMs,
          label: formatForInterval(new Date(keyMs), interval),
          flow1: convertValue(entry.flow1, "flow1", opt.id),
          flow2: convertValue(entry.flow2, "flow2", opt.id),
        } as any;
        if (hasFlow3) {
          base.flow3 = convertValue(entry.flow3, "flow3", opt.id);
        }
        return base;
      });

      const line = filledKeys.map((keyMs) => {
        const entry = rawMap.get(keyMs) || {
          flow1: 0,
          flow2: 0,
          flow3: 0,
          count: 0,
        };
        const count = entry.count || 1;
        const base = {
          timestamp: keyMs,
          label: formatForInterval(new Date(keyMs), interval),
          flowRate1: convertValue(entry.flow1 / count, "flow1", opt.id),
          flowRate2: convertValue(entry.flow2 / count, "flow2", opt.id),
        } as any;
        if (hasFlow3) {
          base.flowRate3 = convertValue(entry.flow3 / count, "flow3", opt.id);
        }
        return base;
      });

      out[opt.id] = { bar, line };
    }

    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowByBlender, blenderIdsKey, interval, unit, start, end]);

  // ---- cumulative totals per material over the displayed period ----
  const perBlenderTotals = useMemo(() => {
    if (!start || !end) return [];

    const cap = completedEnd(interval, end);
    const startAligned = alignStart(start, interval);

    return effectiveBlenderOptions.map((opt) => {
      const rows = flowByBlender[opt.id] ?? [];
      const cost = getCostPerLb(opt.id) as any;
      const labels = getMaterialLabels(opt.id) as any;
      const optHasFlow3 = opt.hasFlow3 ?? defaultHasFlow3(opt.id);
      const keys: Array<"flow1" | "flow2" | "flow3"> = optHasFlow3
        ? ["flow1", "flow2", "flow3"]
        : ["flow1", "flow2"];

      const sumsKg: Record<string, number> = {};
      for (const row of rows) {
        const d = toValidDate(row.timestamp);
        if (!d || !(d < cap)) continue;
        if (isBefore(alignStart(d, interval), startAligned)) continue;
        for (const k of keys) sumsKg[k] = (sumsKg[k] ?? 0) + Number(row[k] || 0);
      }

      // Key by material label so the same material on two blenders merges.
      const values: Record<string, number> = {};
      for (const k of keys) {
        const kg = sumsKg[k] ?? 0;
        const lb = kg * 2.20462;
        const converted =
          unit === "kg" ? kg : unit === "lb" ? lb : lb * (cost[k] ?? 0);
        const label = labels[k] ?? k;
        values[label] = (values[label] ?? 0) + converted;
      }

      return { id: opt.id, label: opt.label, values };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowByBlender, blenderIdsKey, unit, interval, start, end]);

  const formatTotal = (v: number) =>
    unit === "usd"
      ? `$${v.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      : `${v.toLocaleString(undefined, { maximumFractionDigits: 1 })} ${unitLabel}`;

  const applyQuickRange = (hrs: number) => {
    setRangeHrs(hrs);
    setStart(asNashvilleLocal(new Date(Date.now() - hrs * 3600_000)));
    setEnd(nowAsNashvilleLocal());
  };

  const handleIntervalChange = (_: any, newInterval: string | null) => {
    if (newInterval !== null)
      setInterval(newInterval as "hour" | "day" | "week" | "month");
  };

  const downloadCSV = (opt: BlenderOption) => {
    const data = chartsByBlender[opt.id] ?? { bar: [], line: [] };
    const headers = ["timestamp", "flow1", "flow2", "flowRate1", "flowRate2"];
    const rows = data.bar.map((barRow: any, index: number) => {
      const lineRow = data.line[index];
      const ts =
        typeof barRow.timestamp === "number"
          ? formatForInterval(new Date(barRow.timestamp), interval)
          : String(barRow.timestamp);
      return [
        ts,
        barRow.flow1,
        barRow.flow2,
        lineRow?.flowRate1 ?? 0,
        lineRow?.flowRate2 ?? 0,
      ].join(",");
    });

    const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute(
      "download",
      `blender_${opt.id}_flow_${Date.now()}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Charts are stacked full width and grouped by metric: usage for every
  // blender, then rate for every blender.
  return (
    <Grid
      container
      spacing={2}
      padding={{ xs: 1, sm: 2 }}
      maxWidth="xl"
      margin="auto"
    >
      {/* Controls */}
      <Grid
        container
        spacing={2}
        padding={{ xs: 1, sm: 2 }}
        maxWidth="xl"
        margin="auto"
        alignItems="center"
        wrap="wrap"
      >
        <Grid item xs={12} sm={6} md={3}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label="Start Date"
              value={start}
              onChange={(newValue) => {
                setStart(newValue);
                setRangeHrs(null);
              }}
              slots={{ textField: TextField }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: isMobile ? "small" : "medium",
                },
              }}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label="End Date"
              value={end}
              onChange={(newValue) => {
                setEnd(newValue);
                setRangeHrs(null);
              }}
              slots={{ textField: TextField }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: isMobile ? "small" : "medium",
                },
              }}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <ToggleButtonGroup
            value={unit}
            exclusive
            onChange={(_, newUnit) => {
              if (newUnit !== null) setUnit(newUnit);
            }}
            fullWidth
            size={isMobile ? "small" : "medium"}
          >
            <ToggleButton value="kg">kg</ToggleButton>
            <ToggleButton value="lb">lb</ToggleButton>
            <ToggleButton value="usd">$</ToggleButton>
          </ToggleButtonGroup>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Button
            variant="contained"
            fullWidth
            sx={{ height: { xs: 40, sm: "100%" } }}
            onClick={fetchData}
            size={isMobile ? "small" : "medium"}
          >
            Refresh
          </Button>
        </Grid>

        <Grid item xs={12} md={8}>
          <ToggleButtonGroup
            value={rangeHrs != null ? String(rangeHrs) : ""}
            exclusive
            onChange={(_, v) => v && applyQuickRange(Number(v))}
            fullWidth
            size={isMobile ? "small" : "medium"}
          >
            {[1, 4, 12, 24, 72, 168, 720].map((h) => (
              <ToggleButton key={h} value={String(h)}>
                {h < 24 ? `${h}H` : `${h / 24}D`}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary">
            If a date range is set, it overrides the quick range.
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <ToggleButtonGroup
            value={interval}
            exclusive
            onChange={handleIntervalChange}
            fullWidth
            size={isMobile ? "small" : "medium"}
          >
            <ToggleButton value="hour">Hour</ToggleButton>
            <ToggleButton value="day">Day</ToggleButton>
            <ToggleButton value="week">Week</ToggleButton>
            <ToggleButton value="month">Month</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary">
            Grouping interval for all charts.
          </Typography>
        </Grid>
      </Grid>

      {/* Status — one indicator per blender */}
      <Grid item xs={12}>
        <Box
          display="flex"
          flexWrap="wrap"
          gap={2}
          justifyContent="flex-start"
          px={{ xs: 1, sm: 2 }}
        >
          <LoadingOverlay show={statusLoading} minHeight={84}>
            <Box display="flex" flexWrap="wrap" gap={2}>
              {effectiveBlenderOptions.map((opt) => (
                <StatusIndicator
                  key={opt.id}
                  title={opt.label}
                  status={statusById[opt.id] ?? ""}
                />
              ))}
            </Box>
          </LoadingOverlay>
        </Box>
      </Grid>

      {/* Cumulative totals per material over the displayed period */}
      <Grid item xs={12}>
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ px: { xs: 1, sm: 2 }, mb: 1 }}
        >
          Totals for the selected period — all blenders
        </Typography>
        <LoadingOverlay show={loadingFlow} minHeight={110}>
          <MaterialTotalsCards
            blenders={perBlenderTotals}
            formatValue={formatTotal}
          />
        </LoadingOverlay>
      </Grid>

      {/* One card per blender: usage + usage rate for that blender */}
      {effectiveBlenderOptions.map((opt) => (
        <Grid item xs={12} key={`charts-${opt.id}`}>
          <Card>
            <CardContent sx={{ p: { xs: 1.25, sm: 2 } }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {opt.label}
              </Typography>

              <LoadingOverlay
                show={loadingFlow}
                minHeight={isMobile ? 220 : 340}
              >
                <BlenderFlowChart
                  data={chartsByBlender[opt.id]?.bar ?? []}
                  unitLabel={unitLabel}
                  flowLabels={getMaterialLabels(opt.id)}
                  title="Material Usage"
                />
              </LoadingOverlay>

              <Box mt={2} display="flex" justifyContent="center">
                <Button
                  variant="outlined"
                  onClick={() => downloadCSV(opt)}
                  disabled={loadingFlow}
                  size={isMobile ? "small" : "medium"}
                >
                  Export CSV
                </Button>
              </Box>

              <Divider sx={{ my: 2 }} />

              <LoadingOverlay
                show={loadingFlow}
                minHeight={isMobile ? 220 : 340}
              >
                <FlowRateChart
                  data={chartsByBlender[opt.id]?.line ?? []}
                  unitLabel={unitLabel}
                  flowLabels={getMaterialLabels(opt.id)}
                  title="Usage Rate Over Time"
                />
              </LoadingOverlay>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default BlenderFlowDashboard;
